import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2, FileText, Download, Eye, Pencil, Trash2, Save, X } from "lucide-react";
import { vendorsApi, triggerBlobDownload } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { formatFileSize } from "@/lib/fileHelpers";
import { LastChanged, apiError, DOC_CATEGORIES } from "./vendorUtils";
import { DocumentPreviewDialog } from "./DocumentPreviewDialog";

const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp";

const CategorySelect = ({ value, onChange, testId }) => (
  <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
    <SelectTrigger className="w-44" data-testid={testId}><SelectValue placeholder="Kategorie" /></SelectTrigger>
    <SelectContent>
      <SelectItem value="none">Ohne Kategorie</SelectItem>
      {DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
    </SelectContent>
  </Select>
);

export function VendorDocumentsTab({ vendorId, isAdmin, canEdit, onCount }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ title: "", category: "" });
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => setDocs((await vendorsApi.listSub(vendorId, "documents")).data), [vendorId]);
  useEffect(() => { load().catch(() => toast.error("Dokumente konnten nicht geladen werden")); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onCount?.(docs.length); }, [docs.length, onCount]);

  const upload = async () => {
    if (!file) return toast.error("Bitte eine Datei auswählen");
    setUploading(true);
    try {
      const res = await vendorsApi.uploadDocument(vendorId, file, meta.title || file.name, meta.category);
      setDocs((d) => [res.data, ...d]);
      setFile(null);
      setMeta({ title: "", category: "" });
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Dokument hochgeladen");
    } catch (e) {
      toast.error(apiError(e, "Upload fehlgeschlagen"));
    } finally {
      setUploading(false);
    }
  };

  const saveMeta = async () => {
    if (!editing.title.trim()) return toast.error("Titel darf nicht leer sein");
    try {
      const res = await vendorsApi.updateItem(vendorId, "documents", editing.id, { title: editing.title, category: editing.category });
      setDocs((d) => d.map((x) => (x.id === editing.id ? res.data : x)));
      setEditing(null);
      toast.success("Dokument gespeichert");
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    }
  };

  const download = async (doc) => {
    try {
      const res = await vendorsApi.downloadDocument(vendorId, doc.id);
      triggerBlobDownload(res.data, doc.original_filename);
    } catch {
      toast.error("Download fehlgeschlagen");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Dokument „${doc.title}“ wirklich löschen?`)) return;
    try {
      await vendorsApi.deleteItem(vendorId, "documents", doc.id);
      setDocs((d) => d.filter((x) => x.id !== doc.id));
      toast.success("Dokument gelöscht");
    } catch (e) {
      toast.error(apiError(e, "Löschen fehlgeschlagen"));
    }
  };

  return (
    <div className="space-y-4">
      {canEdit && <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Dokument hochladen</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; setFile(f || null); if (f && !meta.title) setMeta((m) => ({ ...m, title: f.name.replace(/\.[^.]+$/, "") })); }} data-testid="vendor-doc-file" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="vendor-doc-pick"><FileText className="w-4 h-4 mr-1.5" />{file ? file.name : "Datei wählen"}</Button>
          <Input className="w-56" placeholder="Titel" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} data-testid="vendor-doc-title" />
          <CategorySelect value={meta.category} onChange={(category) => setMeta({ ...meta, category })} testId="vendor-doc-category" />
          <Button onClick={upload} disabled={uploading || !file} data-testid="vendor-doc-upload">
            {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}Hochladen
          </Button>
          <p className="w-full text-xs text-muted-foreground">PDF, Word, Excel oder Bilder · max. 25 MB</p>
        </CardContent>
      </Card>}
      {docs.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="vendor-docs-empty">Noch keine Dokumente vorhanden.</p>}
      {docs.map((d) => (
        <Card key={d.id} data-testid={`vendor-doc-${d.id}`}>
          <CardContent className="py-4 flex flex-wrap items-center gap-3">
            <FileText className="w-8 h-8 text-primary shrink-0" />
            {editing?.id === d.id ? (
              <div className="flex flex-wrap items-center gap-2 flex-1">
                <Input className="w-56" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid={`vendor-doc-edit-title-${d.id}`} />
                <CategorySelect value={editing.category} onChange={(category) => setEditing({ ...editing, category })} testId={`vendor-doc-edit-category-${d.id}`} />
                <Button size="sm" onClick={saveMeta} data-testid={`vendor-doc-edit-save-${d.id}`}><Save className="w-4 h-4 mr-1" />Speichern</Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <div className="min-w-0 flex-1">
                <p className="font-medium flex flex-wrap items-center gap-2">{d.title}{d.category && <Badge variant="secondary">{d.category}</Badge>}</p>
                <p className="text-xs text-muted-foreground truncate">{d.original_filename} · {formatFileSize(d.size)} · hochgeladen von {d.uploaded_by_name}</p>
                <LastChanged item={d} testId={`vendor-doc-changed-${d.id}`} />
              </div>
            )}
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="icon" title="Vorschau" onClick={() => setPreview(d)} data-testid={`vendor-doc-preview-${d.id}`}><Eye className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" title="Herunterladen" onClick={() => download(d)} data-testid={`vendor-doc-download-${d.id}`}><Download className="w-4 h-4" /></Button>
              {canEdit && <Button variant="ghost" size="icon" title="Bearbeiten" onClick={() => setEditing({ id: d.id, title: d.title, category: d.category })} data-testid={`vendor-doc-edit-${d.id}`}><Pencil className="w-4 h-4" /></Button>}
              {isAdmin && <Button variant="ghost" size="icon" title="Löschen (nur Admin)" onClick={() => remove(d)} data-testid={`vendor-doc-delete-${d.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
            </div>
          </CardContent>
        </Card>
      ))}
      <DocumentPreviewDialog vendorId={vendorId} doc={preview} onClose={() => setPreview(null)} onDownload={download} />
    </div>
  );
}
